import { Injectable } from '@angular/core';

import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil, tap, filter, switchMap } from 'rxjs/operators';

import { ApiService } from '../core/upgraded-providers';

const STEAM_NEWS_ENDPOINT = 'steam/news';
const STEAM_NEWS_LAST_GID = 'steam.news.last.gid';
const STEAM_NEWS_REQUEST_DELAY = 5 * 60 * 1000;

export interface ISteamNewsItem {
    gid: string;
    title: string;
}

@Injectable()
export class SteamNewsService {
    _destroySbj: Subject<void> = new Subject();

    _subscription: Subscription = null;

    constructor(
        private _apiSrv: ApiService
    ) {
    }

    set lastNewsGidRead(gid: string) {
        localStorage.setItem(STEAM_NEWS_LAST_GID, gid || '');
    }

    get lastNewsGidRead(): string {
        try {
            return localStorage.getItem(STEAM_NEWS_LAST_GID) || '';
        } catch (err) {
        }

        return '';
    }

    subscribe(fn: (lastNewsItem: ISteamNewsItem) => void) {
        this._dispose();
        this._destroySbj = new Subject();

        this._subscription = timer(0, STEAM_NEWS_REQUEST_DELAY)
            .pipe(takeUntil(this._destroySbj))
            .pipe(switchMap(() => this.getLastSteamNewsItem()))
            .pipe(filter(({ gid }) => !!gid))
            .pipe(tap((lastNewsItem) => {
                if (!this.lastNewsGidRead) {
                    this.lastNewsGidRead = '0';
                }
            }))
            .pipe(filter(({ gid }) => gid != this.lastNewsGidRead))
            .pipe(tap((lastNewsItem) => fn(lastNewsItem)))
            .subscribe();
    }

    _dispose() {
        if (!this._subscription) {
            return;
        }

        this._destroySbj.next();
        this._subscription.unsubscribe();

        this._subscription = null;
    }

    async getLastSteamNewsItem(): Promise<ISteamNewsItem> {
        try {
            const data = await this._apiSrv.get(STEAM_NEWS_ENDPOINT);
            const [lastNewsItem = {}] = data.appnews && data.appnews.newsitems || [];

            return {
                gid: lastNewsItem.gid || '',
                title: lastNewsItem.title || ''
            };
        } catch (err) {
        }

        return {
            gid: '',
            title: ''
        };
    }
}
